import type {
  ImportRejection,
  ImportViolation,
} from '@/lib/api/generated/model';

import { OUTDATED_DRAFT_MESSAGE } from './builder/draftCopy';

/**
 * A workflow submission the service refused, carrying the full violation list
 * (BR-001-13: every violation, located — never one generic message).
 *
 * Import and rename share this error: the service answers both with the same
 * `ImportRejection` payload, and the dialog and the rename field render
 * `violations` rather than `message`. The `message` fallback exists so an
 * unexpected consumer (an error boundary, a log) still shows something human.
 */
export class WorkflowRejectedError extends Error {
  readonly status: number;
  readonly violations: ImportViolation[];

  constructor(status: number, rejection: ImportRejection) {
    super(
      rejection.violations[0]?.message ??
        'The workflow submission was rejected.',
    );
    this.name = 'WorkflowRejectedError';
    this.status = status;
    this.violations = rejection.violations;
  }
}

/** Narrows a mutation's `unknown` error to the typed rejection (rule 2.5). */
export function isWorkflowRejection(
  error: unknown,
): error is WorkflowRejectedError {
  return error instanceof WorkflowRejectedError;
}

/**
 * Someone else saved this workflow after the builder loaded it.
 *
 * Distinct from a rejection because the document is not wrong — it is merely
 * stale, and the remedy is to reload rather than to fix anything. The builder
 * says so in those terms, and keeps the user's edits on screen while it does:
 * the service wrote nothing, so nothing has been lost yet.
 */
export class WorkflowVersionConflictError extends Error {
  readonly status = 409;

  constructor(message?: string) {
    super(
      message ??
        'This workflow changed since it was opened. Reload it and reapply your changes.',
    );
    this.name = 'WorkflowVersionConflictError';
  }
}

export function isVersionConflict(
  error: unknown,
): error is WorkflowVersionConflictError {
  return error instanceof WorkflowVersionConflictError;
}

/**
 * The save would have removed step(s) that tasks are sitting on.
 *
 * Carries the service's violation list because the user needs to know *which*
 * steps and *which* tasks — "this cannot be saved" without naming the work in
 * the way is a dead end. Extends the rejection so any consumer already
 * rendering `violations` handles it without a second branch, while the distinct
 * class lets the builder point at the offending nodes on the canvas.
 */
export class WorkflowStepsInUseError extends WorkflowRejectedError {
  constructor(status: number, rejection: ImportRejection) {
    super(status, rejection);
    this.name = 'WorkflowStepsInUseError';
  }
}

export function isStepsInUse(error: unknown): error is WorkflowStepsInUseError {
  return error instanceof WorkflowStepsInUseError;
}

/**
 * The workflow is gone — deleted from another tab or by another user.
 *
 * Separate from a conflict because reloading will not help: there is nothing to
 * reload. The builder offers to leave rather than to retry.
 */
export class WorkflowGoneError extends Error {
  readonly status = 404;

  constructor(message?: string) {
    super(message ?? 'This workflow no longer exists.');
    this.name = 'WorkflowGoneError';
  }
}

export function isWorkflowGone(error: unknown): error is WorkflowGoneError {
  return error instanceof WorkflowGoneError;
}

/**
 * The pending changes were started from a published definition that has
 * changed since (spec 144 FR-011), so publishing them would overwrite work
 * nobody looked at.
 *
 * Its own type because the way out is different from a conflict's: reloading
 * alone does not help, since the draft is still the stale one. The author can
 * throw it away, or reload and carry their changes over by hand.
 */
export class PendingChangesOutdatedError extends Error {
  readonly status = 409;

  constructor() {
    super(OUTDATED_DRAFT_MESSAGE);
    this.name = 'PendingChangesOutdatedError';
  }
}

export function isPendingChangesOutdated(
  error: unknown,
): error is PendingChangesOutdatedError {
  return error instanceof PendingChangesOutdatedError;
}
